import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Zap, Moon, Shield, Heart, ArrowRight, Mail, Download } from "lucide-react";

const MensVitalityGuide = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
            <Mail className="w-4 h-4" />
            <span className="text-sm font-medium">Guide Sent to Your Email</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Your Free Vitality Reset Guide
          </h1>
          <p className="text-blue-100 text-lg">
            Check your inbox — your complete guide is on its way. Here's a preview of what's inside.
          </p>
        </div>
      </div>

      {/* Guide Content Preview */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Vitality Reset Guide</h2>
              <p className="text-gray-500 text-sm">For Men Experiencing Early Signs of Decline</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <h3 className="text-xl font-bold text-gray-900 mb-4">What's Inside:</h3>
            
            <div className="space-y-6">
              {/* Section 1 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Understanding What's Happening
                </h4>
                <p className="text-gray-600">
                  After 30, your body's stress response system changes. Cortisol patterns shift, recovery slows, 
                  and the systems that support stamina and confidence need more targeted support. This isn't 
                  failure — it's biology. Understanding this is the first step to addressing it.
                </p>
              </div>

              {/* Section 2 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  The 3 Pillars of Male Vitality
                </h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                    <span><strong>Circulation:</strong> Blood flow affects energy, mental clarity, and physical performance. Poor circulation is often the hidden factor behind fatigue.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                    <span><strong>Stress Resilience:</strong> Your body's ability to handle pressure without burning out. Adaptogens can help restore this capacity.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
                    <span><strong>Hormonal Balance:</strong> Natural testosterone support through lifestyle and targeted botanicals.</span>
                  </li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Daily Habits That Compound
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Morning sunlight exposure (10-15 min) to reset cortisol rhythm</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Cold water finish to showers (30 sec) to boost circulation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Evening screen cutoff (1 hour before bed) for recovery</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Adaptogenic herbs with breakfast for stress resilience</span>
                  </li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  Key Herbs for Male Vitality
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-gray-600">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Ashwagandha</p>
                    <p className="text-sm">Supports stress response and energy levels</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Tongkat Ali</p>
                    <p className="text-sm">Traditional herb for male vitality</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Maca Root</p>
                    <p className="text-sm">Supports stamina and endurance</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Panax Ginseng</p>
                    <p className="text-sm">Energy without crashes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready for Targeted Support?</h3>
          <p className="text-blue-100 mb-6 max-w-lg mx-auto">
            The Vitality Protocol combines these herbs in research-backed dosages, specifically formulated for men experiencing what you described.
          </p>
          <Button
            onClick={() => navigate("/mens-vitality-protocol")}
            className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-semibold rounded-full"
          >
            Explore the Vitality Protocol <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>

        {/* Back Links */}
        <div className="text-center mt-8 space-x-4">
          <Link to="/mens-scorecard" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Retake Assessment
          </Link>
          <Link to="/" className="text-gray-500 hover:text-gray-700 font-medium">
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MensVitalityGuide;
